// src/pages/Dashboard/Admin/SeasonStats/SeasonStatsDashboard.jsx

import React, { useMemo } from "react";

import {
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useUser } from "../../../../pages/Home/UserContext";

import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useSeasonStatsDashboard } from "./hooks/useSeasonStatsDashboard";

import DashboardSeasonStatsHeader from "./components/DashboardSeasonStatsHeader";

import SeasonStatsControlsCard from "./components/SeasonStatsControlsCard";

import SeasonStatsKpiCards from "./components/SeasonStatsKpiCards";

import SeasonEvolutionChart from "./components/SeasonEvolutionChart";

import SeasonBreakdownChart from "./components/SeasonBreakdownChart";

import SeasonGroupsCard from "./components/SeasonGroupsCard";

import SeasonStatsEmptyState from "./components/SeasonStatsEmptyState";

import SeasonStatsEditorDrawer from "./components/SeasonStatsEditorDrawer";

function getStoredUserInfo() {
  try {
    const raw =
      localStorage.getItem("userInfo");

    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Statistiques par saison
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux
              administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SeasonStatsDashboard() {
  useScrollToTop();

  const { user } = useUser();

  const storedUser = useMemo(
    () => getStoredUserInfo(),
    [],
  );

  const roleId = getUserRoleId(
    user || storedUser,
  );

  const {
    seasons,
    comparison,

    selectedSeasonId,
    setSelectedSeasonId,

    currentSeason,
    summary,

    loading,
    saving,
    generating,
    deletingId,

    editorOpen,
    editingSeason,

    openCreateEditor,
    openEditEditor,
    closeEditor,

    handleCreateSeason,
    handleUpdateSeason,
    handleGenerateSeason,
    handleDeleteSeason,

    refreshDashboard,
  } = useSeasonStatsDashboard();

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardSeasonStatsHeader />

        <SeasonStatsControlsCard
          seasons={seasons}
          selectedSeasonId={
            selectedSeasonId
          }
          setSelectedSeasonId={
            setSelectedSeasonId
          }
          currentSeason={currentSeason}
          loading={loading}
          generating={generating}
          deletingId={deletingId}
          onRefresh={refreshDashboard}
          onGenerate={
            handleGenerateSeason
          }
          onAdd={openCreateEditor}
          onEdit={openEditEditor}
          onDelete={
            handleDeleteSeason
          }
        />

        {loading ? (
          <SeasonStatsKpiCards
            summary={summary}
            loading
          />
        ) : seasons.length === 0 ? (
          <SeasonStatsEmptyState
            onGenerate={
              handleGenerateSeason
            }
            onAdd={openCreateEditor}
            generating={generating}
          />
        ) : currentSeason ? (
          <>
            <SeasonStatsKpiCards
              summary={summary}
              loading={false}
            />

            <SeasonEvolutionChart
              comparison={comparison}
            />

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <SeasonBreakdownChart
                seasonStat={
                  currentSeason
                }
                type="GENDER"
                title="Garçons / Filles"
                subtitle="Répartition des adhérents selon le genre enregistré."
                mode="pie"
              />

              <SeasonBreakdownChart
                seasonStat={
                  currentSeason
                }
                type="AGE_RANGE"
                title="Tranches d’âge"
                subtitle="Structure des effectifs selon l’âge des adhérents."
              />
            </div>

            <SeasonBreakdownChart
              seasonStat={currentSeason}
              type="DISCIPLINE"
              title="Disciplines"
              subtitle="Répartition des effectifs selon les disciplines enregistrées."
            />

            <SeasonGroupsCard
              seasonStat={currentSeason}
            />
          </>
        ) : (
          <SeasonStatsEmptyState
            onGenerate={
              handleGenerateSeason
            }
            onAdd={openCreateEditor}
            generating={generating}
          />
        )}
      </div>

      <SeasonStatsEditorDrawer
        isOpen={editorOpen}
        onClose={closeEditor}
        seasonStat={editingSeason}
        saving={saving}
        onCreate={handleCreateSeason}
        onUpdate={handleUpdateSeason}
      />

      <ToastContainer />
    </section>
  );
}